import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { combineLatest, groupBy, map, mergeMap, switchMap } from 'rxjs';

import { Store } from '@ngrx/store';

import { HealthService, UnitConversionService } from '@twaice-fe/frontend/shared/services';
import { HealthV2, HealthAlert, DistributionKpisEnum } from '@twaice-fe/shared/models';
import { healthKpisLatestDataRequestConfig } from '../../../../features/energy-analytics/health/src/lib/kpis-distribution/kpis-distribution.config';
import { KpisDistributionService } from '../../../../features/energy-analytics/health/src/lib/services/kpis-distribution.service';
import { healthAction } from '../actions';

import { selectors } from '@twaice-fe/frontend/shared/store';

const { systemSelectors } = selectors;

@Injectable()
export class HealthEffects {
  init$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(healthAction.fetchHealthKpiData)),
      this.store.select(systemSelectors.getSelected),
    ]).pipe(
      // Group actions by their kpi value
      groupBy(([action]) => action.requestParams.kpi),
      // Handle each kpi group in parallel
      mergeMap((group$) =>
        group$.pipe(
          // Within each kpi group, switch to new requests (cancelling any ongoing ones)
          switchMap(([{ requestParams, customerBk, systemBk, aggregationScope, levelBk }]) =>
            this.healthService
              .getHealthData({
                requestParams,
                customerBk,
                systemBk,
                aggregationScope,
                levelBk,
              })
              .pipe(
                map((health: HealthV2 | HealthAlert) => {
                  const kpiName = health.kpi as DistributionKpisEnum;
                  const kpisChartData: any = {};
                  const kpisChartDataInfo: any = {};
                  const kpisChartMissingDataInfo: any = {};

                  if (health instanceof HealthAlert) {
                    kpisChartData[kpiName] = { alert: health.alert };
                    kpisChartDataInfo[kpiName] = [];
                    kpisChartMissingDataInfo[kpiName] = {};
                  } else {
                    const healthKpi = health;
                    const kpiDistributionData = this.kpisDistributionService.createKpiDistributionData({
                      health: healthKpi,
                      healthRequestParameters: healthKpisLatestDataRequestConfig,
                    });
                    kpisChartData[kpiName] = this.kpisDistributionService.createHealthKpiChartData(kpiDistributionData);
                    this.kpisDistributionService.createHealthKpiTableData([], kpiDistributionData);
                    kpisChartDataInfo[kpiName] = this.kpisDistributionService.createKpiInfoChartData({
                      health: healthKpi,
                      healthRequestParameters: healthKpisLatestDataRequestConfig,
                    });
                    const kpiDataCount = kpiDistributionData?.kpiTableIds
                      ? Object.keys(kpiDistributionData.kpiTableIds).length
                      : 0;
                    const missingDataCount = kpiDistributionData?.kpiChartData
                      ? kpiDataCount - kpiDistributionData?.kpiChartData?.length
                      : 0;
                    kpisChartMissingDataInfo[kpiName] = {
                      dataCount: kpiDataCount,
                      missingDataCount: missingDataCount,
                    };
                  }

                  return healthAction.loadHealthKpiDataSuccess({
                    data: { kpisChartData, kpisChartDataInfo, kpisChartMissingDataInfo },
                  });
                })
              )
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private healthService: HealthService,
    private kpisDistributionService: KpisDistributionService,
    private unitConversionService: UnitConversionService,
    protected store: Store
  ) {}
}
